import type { PaidSupplyCrateId, SupplyCrateCosmeticKind } from "../save/types";

export const SUPPLY_CRATE_UNLOCK_LEVEL = 2;
export const FREE_SUPPLY_COOLDOWN_SECONDS = 21_600;
export const PAID_CRATE_WINDOW_SECONDS = 180;
export const PAID_CRATE_WINDOW_LIMIT = 5;
export const SUPPLY_TICKET_STORAGE_CAP = 5;

export interface PaidSupplyCrateDefinition {
  readonly id: PaidSupplyCrateId;
  readonly name: string;
  readonly basePrice: number;
  readonly cashRange: readonly [number, number];
  readonly jackpotChance: number;
  readonly jackpotRange: readonly [number, number];
  readonly ticketChance: number;
  readonly ticketSeconds: 60 | 120 | 600;
  readonly coreChance: number;
  readonly coreCountRange: readonly [number, number];
  readonly levelPriceShare: number;
  readonly prestigeWealthShare: number;
  readonly cosmeticCashShare: number;
}

export interface CrateCosmeticDefinition {
  readonly kind: SupplyCrateCosmeticKind;
  readonly id: string;
  readonly label: string;
}

export const paidSupplyCrateIds = ["mini", "neon", "jackpot"] as const satisfies readonly PaidSupplyCrateId[];

export const paidSupplyCrateDefinitions: readonly PaidSupplyCrateDefinition[] = [
  {
    id: "mini",
    name: "MINI SUPPLY",
    basePrice: 600,
    cashRange: [0.62, 1.12],
    jackpotChance: 0.075,
    jackpotRange: [1.42, 1.82],
    ticketChance: 0.14,
    ticketSeconds: 60,
    coreChance: 0.26,
    coreCountRange: [1, 3],
    levelPriceShare: 0.029,
    prestigeWealthShare: 0.0012,
    cosmeticCashShare: 0.09
  },
  {
    id: "neon",
    name: "NEON CACHE",
    basePrice: 4_500,
    cashRange: [0.66, 1.2],
    jackpotChance: 0.105,
    jackpotRange: [1.62, 2.25],
    ticketChance: 0.19,
    ticketSeconds: 120,
    coreChance: 0.34,
    coreCountRange: [3, 6],
    levelPriceShare: 0.068,
    prestigeWealthShare: 0.0025,
    cosmeticCashShare: 0.13
  },
  {
    id: "jackpot",
    name: "JACKPOT VAULT",
    basePrice: 31_000,
    cashRange: [0.56, 1.18],
    jackpotChance: 0.115,
    jackpotRange: [2, 2.75],
    ticketChance: 0.24,
    ticketSeconds: 600,
    coreChance: 0.44,
    coreCountRange: [5, 10],
    levelPriceShare: 0.148,
    prestigeWealthShare: 0.0055,
    cosmeticCashShare: 0.18
  }
];

export const crateShellSkins: readonly CrateCosmeticDefinition[] = [
  { kind: "shellSkin", id: "emerald_static", label: "Emerald Static" },
  { kind: "shellSkin", id: "ruby_reactor", label: "Ruby Reactor" },
  { kind: "shellSkin", id: "neon_jackpot", label: "Neon Jackpot" },
  { kind: "shellSkin", id: "blacklight_alloy", label: "Blacklight Alloy" }
];

export const crateCorePatterns: readonly CrateCosmeticDefinition[] = [
  { kind: "corePattern", id: "dice_core", label: "Dice Core" },
  { kind: "corePattern", id: "prism_core", label: "Prism Core" },
  { kind: "corePattern", id: "checker_core", label: "Checker Core" }
];

export const crateCosmetics = [...crateShellSkins, ...crateCorePatterns] as const;

export function getPaidSupplyCrateDefinition(crateId: PaidSupplyCrateId): PaidSupplyCrateDefinition {
  return paidSupplyCrateDefinitions.find((crate) => crate.id === crateId) ?? paidSupplyCrateDefinitions[0];
}

export function isPaidSupplyCrateId(value: string): value is PaidSupplyCrateId {
  return paidSupplyCrateIds.includes(value as PaidSupplyCrateId);
}
